import { useCallback, useEffect, useRef, useState } from 'react';

// `pdfjs-dist` is imported lazily so it only ships with the reviewer chunk.
// We rely on the `?url` import for the worker entry — Vite resolves it to a
// hashed asset under `/assets/...` so CSP-restricted hosts can serve it.
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Public shape — we deliberately stay structural to avoid leaking the
// pdfjs-dist types throughout the component tree.
export interface PdfPageViewport {
  width: number;
  height: number;
  /** Page-relative scale chosen for the current canvas (real px / pdf pt). */
  scale: number;
}

/**
 * Page range (1-based, inclusive, absolute to the loaded PDF) the viewer is allowed to show.
 * Segments of a split upload share the parent's full blob, so bounding the navigation is how
 * "show only this segment" works — the file itself is never sliced.
 */
export interface PageWindow {
  startPage: number;
  endPage: number;
}

export interface UsePdfRendererState {
  isLoading: boolean;
  numPages: number;
  pageNumber: number;
  /** First navigable page — 1 unless a page window narrows it. */
  firstPage: number;
  /** Last navigable page — `numPages` unless a page window narrows it. 0 until the doc loads. */
  lastPage: number;
  /** Last viewport applied to the canvas (used by overlays). */
  viewport: PdfPageViewport | null;
  error: string | null;
}

/**
 * Clamps a requested page window to what the loaded document actually has. Persisted ranges and
 * the real PDF can disagree (e.g. the blob was re-uploaded), so out-of-bounds windows degrade to
 * the nearest valid page instead of breaking navigation. A half-specified window is ignored.
 */
export function resolvePageWindow(
  numPages: number,
  startPage: number | null,
  endPage: number | null,
): { firstPage: number; lastPage: number } {
  if (numPages <= 0) {
    return { firstPage: 1, lastPage: 0 };
  }
  if (startPage === null || endPage === null) {
    return { firstPage: 1, lastPage: numPages };
  }
  const firstPage = Math.min(Math.max(1, startPage), numPages);
  const lastPage = Math.min(Math.max(firstPage, endPage), numPages);
  return { firstPage, lastPage };
}

export interface UsePdfRendererControls {
  setPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
}

// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- typeof dynamic import is the only way to type the lazily-loaded module
type PdfJsModule = typeof import('pdfjs-dist');
let pdfjsPromise: Promise<PdfJsModule> | null = null;

async function loadPdfJs(): Promise<PdfJsModule> {
  if (pdfjsPromise === null) {
    pdfjsPromise = (async () => {
      const mod = await import('pdfjs-dist');
      mod.GlobalWorkerOptions.workerSrc = workerSrc;
      return mod;
    })();
  }
  return pdfjsPromise;
}

/**
 * Loads a PDF from `url` and renders the current page into `canvasRef`.
 * The container width is used to fit-to-width with a high-DPI factor.
 *
 * Returns viewport metadata that consumers (bounding-box overlay) need to
 * align their coordinate system with the canvas pixels.
 *
 * `pageWindow` bounds navigation to a sub-range of the document (see {@link PageWindow}).
 * Page numbers stay absolute throughout — the OCR of a segment keeps the parent's page
 * numbers, so any overlay keyed by page keeps lining up.
 */
export function usePdfRenderer(
  url: string,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  containerRef: React.RefObject<HTMLElement>,
  pageWindow?: PageWindow,
): UsePdfRendererState & UsePdfRendererControls {
  const [state, setState] = useState<UsePdfRendererState>({
    isLoading: true,
    numPages: 0,
    pageNumber: 1,
    firstPage: 1,
    lastPage: 0,
    viewport: null,
    error: null,
  });

  // Primitive deps so an inline `pageWindow` object literal doesn't re-trigger the load effect.
  const windowStart = pageWindow?.startPage ?? null;
  const windowEnd = pageWindow?.endPage ?? null;

  // The pdfjs document handle survives across renders; the proxy is opaque,
  // so we keep it in a ref to avoid re-creating tasks on every state change.
  const docRef = useRef<Awaited<ReturnType<PdfJsModule['getDocument']>['promise']> | null>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);

  const renderPage = useCallback(
    async (pageNumber: number): Promise<void> => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      const doc = docRef.current;
      if (canvas === null || container === null || doc === null) {
        return;
      }

      try {
        const page = await doc.getPage(pageNumber);
        const containerWidth = Math.max(container.clientWidth - 16, 320);
        const baseViewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / baseViewport.width;
        const viewport = page.getViewport({ scale });

        const dpr = window.devicePixelRatio > 0 ? window.devicePixelRatio : 1;
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const ctx = canvas.getContext('2d');
        if (ctx === null) {
          throw new Error('Canvas 2D context not available');
        }
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        if (renderTaskRef.current !== null) {
          renderTaskRef.current.cancel();
        }
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;

        setState((prev) => ({
          ...prev,
          viewport: { width: viewport.width, height: viewport.height, scale },
          isLoading: false,
          error: null,
        }));
      } catch (cause) {
        // `RenderingCancelledException` is expected when we abort an in-flight
        // render to start a new one — silence it.
        if (cause instanceof Error && cause.name === 'RenderingCancelledException') {
          return;
        }
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: cause instanceof Error ? cause.message : 'No se pudo renderizar el PDF.',
        }));
      }
    },
    [canvasRef, containerRef],
  );

  // Load the document.
  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    void (async () => {
      try {
        const pdfjs = await loadPdfJs();
        const loadingTask = pdfjs.getDocument({ url });
        const doc = await loadingTask.promise;
        if (cancelled) {
          void doc.destroy();
          return;
        }
        docRef.current = doc;
        const { firstPage, lastPage } = resolvePageWindow(doc.numPages, windowStart, windowEnd);
        setState((prev) => ({
          ...prev,
          numPages: doc.numPages,
          pageNumber: firstPage,
          firstPage,
          lastPage,
          isLoading: true,
        }));
        await renderPage(firstPage);
      } catch (cause) {
        if (cancelled) {
          return;
        }
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: cause instanceof Error ? cause.message : 'No se pudo cargar el PDF.',
        }));
      }
    })();

    return () => {
      cancelled = true;
      if (renderTaskRef.current !== null) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
      const doc = docRef.current;
      docRef.current = null;
      if (doc !== null) {
        void doc.destroy();
      }
    };
  }, [url, renderPage, windowStart, windowEnd]);

  const setPage = useCallback(
    (page: number) => {
      setState((prev) => {
        const clamped = Math.min(
          Math.max(prev.firstPage, page),
          prev.lastPage > 0 ? prev.lastPage : page,
        );
        if (clamped === prev.pageNumber) {
          return prev;
        }
        void renderPage(clamped);
        return { ...prev, pageNumber: clamped, isLoading: true };
      });
    },
    [renderPage],
  );

  const nextPage = useCallback(() => {
    setState((prev) => {
      if (prev.pageNumber >= prev.lastPage) {
        return prev;
      }
      const target = prev.pageNumber + 1;
      void renderPage(target);
      return { ...prev, pageNumber: target, isLoading: true };
    });
  }, [renderPage]);

  const prevPage = useCallback(() => {
    setState((prev) => {
      if (prev.pageNumber <= prev.firstPage) {
        return prev;
      }
      const target = prev.pageNumber - 1;
      void renderPage(target);
      return { ...prev, pageNumber: target, isLoading: true };
    });
  }, [renderPage]);

  return { ...state, setPage, nextPage, prevPage };
}
